import { useEffect, useState, useRef, useCallback } from 'react'

import type { LoginEvent } from '@space-events/login'
import { loginEventListener } from '@space-events/login'
import type { LogoutEvent } from '@space-events/logout'
import { logoutEventListener } from '@space-events/logout'
import { renewTokenEventListener } from '@space-events/renewToken'

import usePrevious from '@space-shell/common/hooks/usePrevious'
import type { UserType } from '@space-shell/common/services/auth'
import { AuthEvents, AuthService } from '@space-shell/common/services/auth'
import { MonitoringUtils } from '@space-shell/common/utils/monitoring'
import { queryClient } from '@space-shell/config/request'
import { useAuthStore, useViewAsStore } from '@space-shell/core/store'

import { AuthContext } from './context'
import type { AuthProviderProps } from './types'
import { AuthStatus } from './types'

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.AUTHENTICATING)
  const { setAuth, token } = useAuthStore()
  const prevToken = usePrevious(token)
  const authStatusRef = useRef<AuthStatus>(AuthStatus.AUTHENTICATING)

  const updateUser = useCallback(
    (user: UserType) => {
      setAuth({
        baseUrl: '',
        id: user?.profile.attributes.id ?? '',
        token: user?.id_token ?? '',
        expires_at: user?.expires_at ?? 0,
        locale: user?.profile.locale ?? '',
        userName: user?.profile.name ?? '',
        userSocialName: user?.profile?.tradeName ?? '',
        authorities: user?.profile.authorities ?? [],
        tokenCreatedAt: user?.profile.auth_time ?? 0,
        ucode: user?.profile.ucode ?? '',
        sid: user?.profile.sid ?? '',
        email: user?.profile.email ?? ''
      })
      authStatusRef.current = AuthStatus.AUTHENTICATED
      setAuthStatus(authStatusRef.current)
    },
    [setAuth]
  )

  useEffect(() => {
    const userListener = (event: CustomEvent<UserType>) => {
      const user = event.detail

      if (!user) {
        authStatusRef.current = AuthStatus.UNAUTHENTICATED
        setAuthStatus(authStatusRef.current)

        return
      }

      updateUser(user)
    }

    AuthEvents.addListenUser(userListener)

    return () => {
      AuthEvents.removeListenUser(userListener)
    }
  }, [updateUser])

  useEffect(() => {
    if (prevToken !== token) {
      useViewAsStore.setState({ isEnabled: false })
      queryClient.clear()
    }
  }, [prevToken, token])

  useEffect(() => {
    const renewErrorListener = (event: CustomEvent<unknown>) => {
      setAuthStatus(AuthStatus.UNAUTHENTICATED)
      MonitoringUtils.trace({ message: `[AUTH] Renew Error: ${event.detail}` })

      if (event.detail !== 'login_required') {
        AuthService.signOut({
          origin: window.location.href,
          message: `CAS renew error ${event.detail}`
        })
      }
    }

    AuthEvents.addListenRenewError(renewErrorListener)

    return () => {
      AuthEvents.removeListenRenewError(renewErrorListener)
    }
  }, [])

  useEffect(() => {
    const renewToken = async () => {
      if (authStatusRef.current === AuthStatus.RENEWING) {
        MonitoringUtils.trace({ message: `[AUTH] Renewing`, level: 'info' })

        return
      }
      authStatusRef.current = AuthStatus.RENEWING
      setAuthStatus(authStatusRef.current)

      const user = await AuthService.silentRenew()

      if (!user) {
        MonitoringUtils.trace({ message: `[AUTH] Renew Failed & Logout`, level: 'info' })
        authStatusRef.current = AuthStatus.UNAUTHENTICATED
        AuthService.signOut({
          origin: window.location.href,
          message: `CAS renew error and logout`
        })

        return
      }

      MonitoringUtils.trace({ message: `[AUTH] Renew Success`, level: 'info' })
      updateUser(user)
    }

    const login = (event: LoginEvent) => {
      AuthService.start({ signInRedirect: event?.detail?.signInRedirect })
    }

    const logout = (event?: LogoutEvent) => {
      if (authStatusRef.current === AuthStatus.UNAUTHENTICATED) {
        MonitoringUtils.trace({ message: `[AUTH] Logout`, level: 'info' })

        return
      }
      authStatusRef.current = AuthStatus.UNAUTHENTICATED
      setAuthStatus(authStatusRef.current)
      AuthService.signOut(event?.detail)
    }

    const unsubscribeRenew = renewTokenEventListener(renewToken)
    const unsubscribeLogin = loginEventListener(login)
    const unsubscribeLogout = logoutEventListener(logout)

    return () => {
      unsubscribeRenew()
      unsubscribeLogin()
      unsubscribeLogout()
    }
  }, [updateUser])

  return (
    <AuthContext.Provider value={{ authStatus, setAuthStatus }}>{children}</AuthContext.Provider>
  )
}
